// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

/** Page and zoom state of the Enlarged_Viewer, as a pure reducer. */

export interface ViewerState {
  page: number;
  pageCount: number;
  /** Zoom in percent of the fitted width. */
  zoom: number;
}

export type ViewerAction =
  | { type: 'next' }
  | { type: 'previous' }
  | { type: 'zoomIn' }
  | { type: 'zoomOut' }
  | { type: 'fit' }
  | { type: 'goTo'; page: number };

export const ZOOM_MIN = 50;
export const ZOOM_MAX = 400;
export const ZOOM_STEP = 25;
export const ZOOM_FIT = 100;

/** True when `page` is an integer in `1..pageCount`. */
export function inPageRange(page: number, pageCount: number): boolean {
  return Number.isInteger(page) && page >= 1 && page <= pageCount;
}

export function viewerReducer(s: ViewerState, a: ViewerAction): ViewerState {
  switch (a.type) {
    case 'next':
      return s.page < s.pageCount ? { ...s, page: s.page + 1 } : s;
    case 'previous':
      return s.page > 1 ? { ...s, page: s.page - 1 } : s;
    case 'zoomIn':
      return s.zoom + ZOOM_STEP <= ZOOM_MAX ? { ...s, zoom: s.zoom + ZOOM_STEP } : s;
    case 'zoomOut':
      return s.zoom - ZOOM_STEP >= ZOOM_MIN ? { ...s, zoom: s.zoom - ZOOM_STEP } : s;
    case 'fit':
      return s.zoom === ZOOM_FIT ? s : { ...s, zoom: ZOOM_FIT };
    case 'goTo':
      return inPageRange(a.page, s.pageCount) && a.page !== s.page ? { ...s, page: a.page } : s;
  }
}
